import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import CalendarPage from './pages/CalendarPage';
import AgendaClinicaPage from './pages/AgendaClinicaPage';
import AppointmentDetailPage from './pages/AppointmentDetailPage';
import PatientsPage from './pages/PatientsPage';
import PatientDetailPage from './pages/PatientDetailPage';
import PatientEvaluationsPage from './pages/PatientEvaluationsPage';
import EvaluacionKanbanPage from './pages/EvaluacionKanbanPage';
import AvailabilityPage from './pages/AvailabilityPage';
import WhatsAppConversationsPage from './pages/WhatsAppConversationsPage';
import UsersPage from './pages/UsersPage';
import SurgeryCRMPage from './pages/SurgeryCRMPage';
import SurgeryInterestedDetailPage from './pages/SurgeryInterestedDetailPage';
import InventoryPage from './pages/InventoryPage';
import SuppliersPage from './pages/SuppliersPage';
import PurchasesPage from './pages/PurchasesPage';
import NewPurchasePage from './pages/NewPurchasePage';
import PurchaseDetailPage from './pages/PurchaseDetailPage';
import PaymentAgendaPage from './pages/PaymentAgendaPage';
import SalesPage from './pages/SalesPage';
import NewSalePage from './pages/NewSalePage';
import SaleDetailPage from './pages/SaleDetailPage';
import SalesCatalogPage from './pages/SalesCatalogPage';
import FinancePage from './pages/FinancePage';
import EmailSettingsPage from './pages/EmailSettingsPage';
import NoShowPage from './pages/NoShowPage';
import BulkImportPage from './pages/BulkImportPage';
import PublicBookingPage from './pages/Public/PublicBookingPage';

// Layouts
import AdminLayout from './layouts/AdminLayout';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Booking Route - NO AUTH REQUIRED */}
      <Route path="/agendar" element={<PublicBookingPage />} />
      
      {/* Redirect root to login */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      {/* Auth Route */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      {/* Reset Password Route (Public) */}
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        } 
      />
      
      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="calendar" element={<AgendaClinicaPage />} />
        <Route path="no-asistieron" element={<NoShowPage />} />
        <Route path="appointments/:id" element={<AppointmentDetailPage />} />
        <Route path="patients" element={<PatientsPage />} />
        <Route path="patients/:id" element={<PatientDetailPage />} />
        <Route path="patients/:patientId/evaluations" element={<PatientEvaluationsPage />} />
        <Route path="patients/:patientId/evaluacion-kanban" element={<EvaluacionKanbanPage />} />
        <Route path="settings/availability" element={<AvailabilityPage />} />
        <Route path="settings/users" element={<UsersPage />} />
        <Route path="whatsapp" element={<WhatsAppConversationsPage />} />
        <Route path="crm-cirugias" element={<SurgeryCRMPage />} />
        <Route path="crm-cirugias/:id" element={<SurgeryInterestedDetailPage />} />
        <Route path="inventario" element={<InventoryPage />} />
        <Route path="proveedores" element={<SuppliersPage />} />
        <Route path="compras" element={<PurchasesPage />} />
        <Route path="compras/nueva" element={<NewPurchasePage />} />
        <Route path="compras/agenda" element={<PaymentAgendaPage />} />
        <Route path="compras/:id" element={<PurchaseDetailPage />} />
        <Route path="ventas" element={<SalesPage />} />
        <Route path="ventas/nueva" element={<NewSalePage />} />
        <Route path="ventas/configuracion" element={<SalesCatalogPage />} />
        <Route path="ventas/:id" element={<SaleDetailPage />} />
        <Route path="finanzas" element={<FinancePage />} />
        <Route path="settings/email" element={<EmailSettingsPage />} />
        <Route path="importacion" element={<BulkImportPage />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
